import { apiClient } from '@features/shared/api/client'
import type { ChatAttachment, ChatAttachmentLimits } from './contracts'

// A 20 MB PDF on a slow connection takes longer than the API client's 30 s.
const UPLOAD_TIMEOUT_MS = 5 * 60 * 1000

/** A presigned S3 URL is absolute already; the API's own signed path (local
 * development) goes under its base, as an export's link does. */
function absoluteUrl(url: string | null | undefined): string | null {
  if (!url) return null
  if (/^https?:\/\//i.test(url)) return url
  const base = (apiClient.defaults.baseURL ?? '').replace(/\/$/, '')
  return new URL(`${base}${url}`, window.location.origin).toString()
}

function withAbsoluteLinks(attachment: ChatAttachment): ChatAttachment {
  return {
    ...attachment,
    url: absoluteUrl(attachment.url),
    thumbnail_url: absoluteUrl(attachment.thumbnail_url),
  }
}

export async function getChatAttachmentLimits(): Promise<ChatAttachmentLimits> {
  const { data } = await apiClient.get<ChatAttachmentLimits>('/assistant/attachments/limits')
  return data
}

/** Uploads one file; it waits on the server until a question carries it. */
export async function uploadChatAttachment(
  file: File,
  options: { signal?: AbortSignal; onProgress?: (fraction: number) => void } = {}
): Promise<ChatAttachment> {
  const form = new FormData()
  form.append('file', file, file.name)
  // postForm, not post: the client's JSON Content-Type would make axios send
  // the form as JSON, with no file. postForm sends multipart, and the browser
  // adds the boundary.
  const { data } = await apiClient.postForm<ChatAttachment>('/assistant/attachments', form, {
    signal: options.signal,
    timeout: UPLOAD_TIMEOUT_MS,
    onUploadProgress: (event) => {
      if (event.total) options.onProgress?.(event.loaded / event.total)
    },
  })
  return withAbsoluteLinks(data)
}

/** The attachment with fresh links, for opening it. */
export async function getChatAttachment(attachmentId: string): Promise<ChatAttachment> {
  const { data } = await apiClient.get<ChatAttachment>(
    `/assistant/attachments/${encodeURIComponent(attachmentId)}`
  )
  return withAbsoluteLinks(data)
}

/** Takes an upload off the composer before it's sent. */
export async function removeChatAttachment(attachmentId: string): Promise<void> {
  await apiClient.delete(`/assistant/attachments/${encodeURIComponent(attachmentId)}`)
}

export { withAbsoluteLinks as chatAttachmentWithAbsoluteLinks }
